import { useMemo, useState } from 'react';
import { CalendarIcon, CheckSquareIcon, HistoryIcon, MailIcon, PhoneIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Button,
  EmptyState,
  ErrorNotice,
  FactList,
  Heading,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  SkeletonTable,
  Tabs,
  useReturnTo,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { formatMoney } from '@/lib/cash-flow';
import { OWNER_PARAM, opensLabel, outreachEmailPath } from '@/lib/outreach-email';
import {
  CLIENT_ACTIVITY_KIND,
  QUEUE_STATUS,
  REPEAT_CLIENTS_PATH,
  readRepeatClient,
  repeatClientQueryKey,
  type ClientActivity,
  type ClientActivityKind,
  type RepeatClientDetail,
} from '@/lib/repeat-clients';
import { placeOf, type ContactRow } from '@/lib/sales-data';
import { formatDay } from '@/lib/sales-workspace';
import { DealTable } from '@/components/sales-workspace/sales-records';
import { STATUS_LABEL } from '@/pages/RepeatClientsPage';

const ALL = 'all';
type ActivityFilter = ClientActivityKind | typeof ALL;
const ACTIVITY_TABS: ReadonlyArray<{ readonly key: ActivityFilter; readonly label: string }> = [
  { key: ALL, label: 'All' },
  { key: CLIENT_ACTIVITY_KIND.email, label: 'Emails' },
  { key: CLIENT_ACTIVITY_KIND.call, label: 'Calls' },
  { key: CLIENT_ACTIVITY_KIND.meeting, label: 'Meetings' },
  { key: CLIENT_ACTIVITY_KIND.todo, label: 'To-dos' },
];
const KIND_LABEL: Readonly<Record<ClientActivityKind, string>> = {
  [CLIENT_ACTIVITY_KIND.email]: 'Email',
  [CLIENT_ACTIVITY_KIND.call]: 'Call',
  [CLIENT_ACTIVITY_KIND.meeting]: 'Meeting',
  [CLIENT_ACTIVITY_KIND.todo]: 'To-do',
};
const KIND_ICON: Readonly<Record<ClientActivityKind, typeof MailIcon>> = {
  [CLIENT_ACTIVITY_KIND.email]: MailIcon,
  [CLIENT_ACTIVITY_KIND.call]: PhoneIcon,
  [CLIENT_ACTIVITY_KIND.meeting]: CalendarIcon,
  [CLIENT_ACTIVITY_KIND.todo]: CheckSquareIcon,
};
const WHEN = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

function Section({ title, description, children }: { readonly title: string; readonly description?: string; readonly children: React.ReactNode }): React.ReactElement {
  return (
    <section className="mt-6">
      <Heading variant="card" as="h2">
        {title}
      </Heading>
      {description ? <p className="mb-3 text-portal-micro text-portal-muted">{description}</p> : <div className="mb-3" />}
      {children}
    </section>
  );
}

/**
 * Repeat clients → one client: who they are, every deal they've had, every
 * email, call and meeting, the emails sent from bcecloud with their opens,
 * and their people. Opened by clicking the client's row.
 *
 * @returns The page.
 */
export function RepeatClientPage(): React.ReactElement {
  const { accountId = '' } = useParams();
  const [params] = useSearchParams();
  const ownerId = params.get(OWNER_PARAM) ?? undefined;
  const backTo = useReturnTo(REPEAT_CLIENTS_PATH);
  const detail = useQuery({ queryKey: repeatClientQueryKey(accountId), queryFn: ({ signal }) => readRepeatClient(accountId, signal) });

  if (detail.isPending) {
    return (
      <section className="animate-portal-rise">
        <SkeletonTable rows={6} columns={['w-[30%]', 'w-[50%]']} label="Loading the client…" />
      </section>
    );
  }
  if (detail.isError) {
    return (
      <section className="animate-portal-rise">
        <PageHeading eyebrow="REPEAT CLIENTS" backTo={backTo} title="Client" />
        <ErrorNotice title="Client unavailable" message={errorMessage(detail.error)} />
      </section>
    );
  }
  return <ClientView data={detail.data} backTo={backTo} ownerId={ownerId} />;
}

function ClientView({ data, backTo, ownerId }: { readonly data: RepeatClientDetail; readonly backTo: string; readonly ownerId: string | undefined }): React.ReactElement {
  const navigate = useNavigate();
  const { account, openDeals, pastBookings, otherClosedDeals, contacts, activity, sentEmails } = data;
  const deals = [...openDeals, ...pastBookings, ...otherClosedDeals];
  const bookedValue = pastBookings.reduce((sum, deal) => sum + Math.max(0, deal.totalCost ?? 0), 0);
  const lastBooking = pastBookings[0];
  const lastEmail = activity.find((item) => item.kind === CLIENT_ACTIVITY_KIND.email);
  const lastCall = activity.find((item) => item.kind === CLIENT_ACTIVITY_KIND.call);
  const statusLabel = data.status === QUEUE_STATUS.booked ? 'Booked' : STATUS_LABEL[data.status];

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="REPEAT CLIENTS"
        backTo={backTo}
        title={account.name || 'Untitled account'}
        subtitle={[placeOf(account.billingCity, account.billingState), account.industry, account.ownerName && `Owner: ${account.ownerName}`].filter(Boolean).join(' · ')}
        action={
          <Button onClick={() => navigate(outreachEmailPath(account.id, ownerId))}>
            <MailIcon aria-hidden className="size-4" /> Email this client
          </Button>
        }
      />

      <Panel className="p-5">
        <FactList
          columns={4}
          facts={[
            { label: 'Status', value: <Pill tone={data.status === QUEUE_STATUS.toContact ? 'brand' : 'neutral'}>{data.snoozedUntil ? `Snoozed until ${formatDay(data.snoozedUntil, { withYear: true })}` : statusLabel}</Pill> },
            { label: 'Bookings', value: pastBookings.length },
            { label: 'Booked value', value: bookedValue > 0 ? formatMoney(bookedValue) : '—' },
            { label: 'Last event', value: formatDay(lastBooking?.eventDate ?? null, { withYear: true }) },
            { label: 'Open deals', value: openDeals.length },
            { label: 'Last email', value: lastEmail ? WHEN.format(new Date(lastEmail.at)) : '—' },
            { label: 'Last call', value: lastCall ? WHEN.format(new Date(lastCall.at)) : '—' },
            { label: 'Phone', value: account.phone ? <a href={`tel:${account.phone}`} className="text-brand hover:underline">{account.phone}</a> : '—' },
          ]}
        />
      </Panel>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
        <div className="min-w-0">
          <Section title="Activity" description={data.activityLive ? 'Every email, call, meeting and to-do in Salesforce, newest first. Subjects only.' : 'Salesforce could not be reached, so only activity logged on deals shows.'}>
            <ActivityList items={activity} />
          </Section>
          <Section title="Opportunities" description="Every deal they’ve had, open and closed.">
            <DealTable
              deals={deals}
              prefix="deals"
              defaultSort={{ key: 'eventDate', direction: 'desc' }}
              emptyTitle="No opportunities"
              emptyMessage="Deals on this account show here."
            />
          </Section>
        </div>
        <div className="min-w-0">
          <Section title="Sent from bcecloud" description="Opens count when their mail loads images, so treat them as a hint.">
            <Panel>
              {sentEmails.length === 0 ? (
                <EmptyState message="Emails you send this client from bcecloud show here with their opens." />
              ) : (
                <ul className="divide-y divide-portal-line">
                  {sentEmails.map((email) => (
                    <li key={email.id} className="p-4">
                      <p className="text-portal-small font-bold text-portal-ink">{email.subject}</p>
                      <p className="text-portal-micro text-portal-muted">
                        {WHEN.format(new Date(email.sentAt))} to {email.to}
                      </p>
                      <Pill tone={email.opens > 0 ? 'success' : 'neutral'} className="mt-2">
                        {opensLabel(email.opens)}
                      </Pill>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </Section>
          <Section title="People">
            <ContactList contacts={contacts} />
          </Section>
        </div>
      </div>
    </section>
  );
}

function ActivityList({ items }: { readonly items: readonly ClientActivity[] }): React.ReactElement {
  const [filter, setFilter] = useState<ActivityFilter>(ALL);
  const [search, setSearch] = useState('');
  const counts = useMemo(() => {
    const byKind = new Map<ActivityFilter, number>([[ALL, items.length]]);
    for (const item of items) byKind.set(item.kind, (byKind.get(item.kind) ?? 0) + 1);
    return byKind;
  }, [items]);
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = items.filter(
    (item) =>
      (filter === ALL || item.kind === filter) &&
      words.every((word) => [item.title, item.contactName, item.dealName, item.ownerName].join(' ').toLowerCase().includes(word)),
  );

  return (
    <Panel className="p-5">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Tabs label="Which activity to show" active={filter} onChange={setFilter} items={ACTIVITY_TABS.map((tab) => ({ ...tab, count: counts.get(tab.key) ?? 0 }))} />
        <SearchInput label="Search activity" placeholder="Subject, person or deal" value={search} onChange={(event) => setSearch(event.target.value)} className="ml-auto w-full max-w-xs" />
      </div>
      {shown.length === 0 ? (
        <EmptyState
          icon={<HistoryIcon aria-hidden className="size-6" />}
          title={items.length === 0 ? 'No activity yet' : 'Nothing matches'}
          message={items.length === 0 ? 'Emails, calls and meetings logged in Salesforce show here.' : 'Try another tab or search.'}
        />
      ) : (
        <ul>
          {shown.map((item) => {
            const Icon = KIND_ICON[item.kind];
            return (
              <li key={item.id} className="flex gap-3 border-t border-portal-line py-3 first:border-t-0 first:pt-0">
                <span className="mt-0.5 text-portal-muted">
                  <Icon aria-hidden className="size-4" />
                </span>
                <span className="min-w-0">
                  <span className="block text-portal-small">{item.title}</span>
                  <span className="block text-portal-micro text-portal-muted">
                    {[KIND_LABEL[item.kind], WHEN.format(new Date(item.at)), item.contactName && `with ${item.contactName}`, item.ownerName && `by ${item.ownerName}`, item.dealName]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}

function ContactList({ contacts }: { readonly contacts: readonly ContactRow[] }): React.ReactElement {
  if (contacts.length === 0) {
    return (
      <Panel>
        <EmptyState message="Contacts on this account in Salesforce show here." />
      </Panel>
    );
  }
  return (
    <Panel>
      <ul className="divide-y divide-portal-line">
        {contacts.map((contact) => (
          <li key={contact.id} className="p-4">
            <p className="text-portal-small font-bold text-portal-ink">{contact.name || contact.email}</p>
            {contact.title ? <p className="text-portal-micro text-portal-muted">{contact.title}</p> : null}
            <p className="mt-1 text-portal-micro">
              {contact.email ? (
                <a href={`mailto:${contact.email}`} className="text-brand hover:underline">
                  {contact.email}
                </a>
              ) : null}
              {contact.email && contact.phone ? ' · ' : null}
              {contact.phone ? (
                <a href={`tel:${contact.phone}`} className="text-brand hover:underline">
                  {contact.phone}
                </a>
              ) : null}
            </p>
            {contact.emailOptOut || contact.doNotCall ? (
              <div className="mt-2 flex gap-2">
                {contact.emailOptOut ? <Pill tone="warning">No email</Pill> : null}
                {contact.doNotCall ? <Pill tone="warning">Do not call</Pill> : null}
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </Panel>
  );
}
